import { ChakraProvider } from '@chakra-ui/react'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import theme from './themes/index.ts'
import './i18n/config'

// Initialize Sentry (disabled on localhost)
// Pairing links contain private demo credentials; do not instrument those pages or record their QR codes.
// A calendar return carries a short-lived authorization code, not a page to trace/replay.
const calendarConsentReturn = new URLSearchParams(window.location.search).has('calendar_sync') ||
  new URLSearchParams(window.location.hash.slice(1)).has('calendar_code')
if (!calendarConsentReturn && !window.location.pathname.includes('/office-checkin') && !window.location.hostname.includes('localhost') && !window.location.hostname.includes('127.0.0.1')) {
  Sentry.init({
  dsn: "https://188c31c637c501b84841cf46c210ceba@o4510385499537408.ingest.us.sentry.io/4510385521754112",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration()
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
  // Enable logs to be sent to Sentry
  enableLogs: true
});
}

// Auto-reload on stale chunk errors (Fixes FRONTEND-1E)
// After deploy, cached index.html may reference old chunk hashes that no longer exist.
// Safari surfaces this as "Importing a module script failed". Force a one-time reload.
window.addEventListener('error', (event) => {
  const msg = event.message || ''
  if (
    msg.includes('Importing a module script failed') ||
    msg.includes('Failed to fetch dynamically imported module')
  ) {
    const lastReload = sessionStorage.getItem('chunk_error_reload')
    if (!lastReload || Date.now() - Number(lastReload) > 10000) {
      sessionStorage.setItem('chunk_error_reload', String(Date.now()))
      window.location.reload()
    }
  }
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
})

createRoot(document.getElementById('root')!).render(
  <QueryClientProvider client={queryClient}>
    <ChakraProvider theme={theme}>
      <App />
    </ChakraProvider>
  </QueryClientProvider>
)
